/** One student (STORY-061): mastery per objective, a timeline of every scored answer, and
 * check-in ideas. The name comes from the session roster; analytics only knows the person id.
 *
 * STORY-105: the timeline endpoint pages (500 newest answers, then a `next_before` cursor),
 * so this screen reads it page by page. Everything derived from the answers -- the trail and
 * the dot row beside each objective -- covers exactly the pages loaded, and the line under
 * the trail says how many that is; the objective's own `n` is the all-time count and comes
 * from mastery, not from these rows. */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { TeacherChrome } from "../teacher/Chrome";
import { getCourse, getRoster, getTimeline, isOfflineError } from "../teacher/api";
import { Button } from "../ui";

const GLYPH: Record<string, string> = { secure: "■", developing: "▦", not_yet: "□", unassessed: "·" };

export function StudentDetail() {
  const { courseId, personId } = useParams({ strict: false }) as { courseId: string; personId: string };
  const { t, i18n } = useTranslation();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const roster = useQuery({ queryKey: ["roster", courseId], queryFn: () => getRoster(courseId) });
  const tl = useInfiniteQuery({
    queryKey: ["timeline", courseId, personId],
    queryFn: ({ pageParam }) => getTimeline(courseId, personId, pageParam),
    initialPageParam: undefined as string | undefined,
    // pages come newest first; each page is oldest-first inside, so the whole trail is the
    // pages reversed and concatenated
    getNextPageParam: (last) => last.next_before ?? undefined,
  });
  const head = tl.data?.pages[0];
  const events = (tl.data?.pages ?? []).slice().reverse().flatMap((p) => p.events);
  const name = roster.data?.find((p) => p.person_id === personId)?.label || t("student.unknown");
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const los = head?.los ?? [];
  const bands = { secure: 0, developing: 0, not_yet: 0, unassessed: 0 };
  for (const lo of los) bands[lo.band as keyof typeof bands] += 1;
  const sessions = Array.from(new Set(events.map((e) => e.session_id)));
  const notYet = los
    .filter((lo) => lo.band === "not_yet")
    .sort((a, b) => (a.last_assessed_at ?? "").localeCompare(b.last_assessed_at ?? ""));

  return (
    <TeacherChrome title={head || roster.data ? name : t("student.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="dashboard" wide>
      <header className="-mt-2 flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
          <Link to="/t/c/$courseId/dashboard" params={{ courseId }} className="underline">{t("student.backToDashboard")}</Link>
          <p className="measure text-[color:var(--ink-2)]">{t("student.lede")}</p>
        </div>
        {head && <p className="text-sm">{t("student.summary", { secure: bands.secure, developing: bands.developing, notYet: bands.not_yet, unassessed: bands.unassessed })}</p>}
      </header>
      {tl.isError && <p className={card}>{t(isOfflineError(tl.error) ? "dash.needsServer" : "dash.notProjected")}</p>}
      {head && (
        <section className={card}>
          <h2 className="font-semibold">{t("student.checkIn")}</h2>
          <p className="measure mb-2 text-xs text-[color:var(--ink-2)]">{t("student.checkInHint")}</p>
          {notYet.length === 0 ? (
            <p className="text-sm">{t("student.none")}</p>
          ) : (
            <ul className="grid-fit text-sm" style={{ "--fit-min": "22rem", gap: "0.25rem 1.5rem" } as CSSProperties}>
              {notYet.slice(0, 5).map((lo) => (
                <li key={lo.lo_id}><span className="mr-1 font-mono text-xs">{lo.code}</span>{lo.text}</li>
              ))}
            </ul>
          )}
        </section>
      )}
      {/* The two long lists share the width: objectives beside the answer trail. */}
      {head && (
        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <section className={card}>
            <h2 className="mb-2 font-semibold">{t("student.objectives")}</h2>
            <ul className="flex flex-col gap-2 text-sm">
              {los.map((lo) => {
                const dots = sessions.map((sid) => events.filter((e) => e.session_id === sid && e.lo_id === lo.lo_id));
                return (
                  <li key={lo.lo_id} className="flex items-start gap-3">
                    <span aria-hidden className={`cell cell-${lo.band} !min-w-6 !max-w-6`}>{GLYPH[lo.band]}</span>
                    <div className="min-w-0 flex-1">
                      <p><span className="mr-2 font-mono text-xs text-[color:var(--ink-2)]">{lo.code}</span>{lo.text}</p>
                      <p className="flex flex-wrap items-center gap-1 text-xs text-[color:var(--ink-2)]">
                        <span>{t(`dash.bands.${lo.band}`)}</span>
                        {lo.n > 0 && <span>· {t("dash.n", { count: lo.n })}</span>}
                        <span className="ml-2 inline-flex gap-0.5" aria-hidden>
                          {dots.map((evs, i) => (
                            <span key={i} className="inline-flex gap-px">
                              {evs.length === 0 ? <span className="opacity-30">·</span> : evs.map((e, j) => <span key={j} style={{ color: e.correct ? "var(--secure)" : "var(--not-yet)" }}>{e.correct ? "●" : "○"}</span>)}
                            </span>
                          ))}
                        </span>
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
          <section className={card}>
            <h2 className="mb-2 font-semibold">{t("student.timeline")}</h2>
              {events.length === 0 ? (
                <p className="text-sm text-[color:var(--ink-2)]">{t("student.empty")}</p>
              ) : (
                <ol className="flex flex-col gap-2 text-sm">
                  {[...events].reverse().map((e) => (
                    <li key={`${e.session_id}-${e.item_id}-${e.attempt}`} className="flex gap-2">
                      <span aria-hidden style={{ color: e.correct ? "var(--secure)" : "var(--not-yet)" }}>{e.correct ? "✓" : "✗"}</span>
                      <div className="min-w-0">
                        <p className="line-clamp-2">{e.stem}</p>
                        <p className="text-xs text-[color:var(--ink-2)]">
                          {e.lo_code && <span className="mr-1 font-mono">{e.lo_code}</span>}
                          {fmt(e.occurred_at)}{e.attempt > 1 ? ` · ${t("student.attempt", { n: e.attempt })}` : ""}
                          {e.confidence !== null ? ` · ${t("dash.confidence", { n: e.confidence })}` : ""}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              {tl.hasNextPage && (
                <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-[color:var(--ink-2)]">
                  <span>{t("student.partial", { n: events.length })}</span>
                  <Button variant="tertiary" size="sm" className="px-0" loading={tl.isFetchingNextPage} onClick={() => void tl.fetchNextPage()}>
                    {t("student.earlier")}
                  </Button>
                </p>
              )}
            </section>
        </div>
      )}
    </TeacherChrome>
  );
}
